import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { useSettings } from "@/lib/settings";

export default function InvoicePrintPage() {
  const { id } = useParams();
  const { settings } = useSettings();
  const [inv, setInv] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get(`/admin/invoices/${id}`).then(r => setInv(r.data)).catch(() => {}); }, [id]);

  const emailInvoice = async () => {
    if (!inv?.customer_email) { toast.error("This invoice has no customer email on file"); return; }
    setSending(true);
    try {
      await api.post(`/admin/invoices/${id}/email`);
      toast.success(`Invoice emailed to ${inv.customer_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send — check email is configured (Resend API key) in the backend");
    } finally { setSending(false); }
  };
  const printIt = () => window.print();

  if (!inv) return <div className="p-16 text-center text-muted-foreground">Loading invoice…</div>;

  return (
    <div className="max-w-3xl mx-auto p-6 md:p-10 print:p-0 print:max-w-none">
      <div className="flex justify-end gap-2 mb-4 print:hidden">
        <Button variant="outline" onClick={emailInvoice} disabled={sending} data-testid="invoice-email-btn"><Mail className="h-4 w-4 mr-2" />{sending ? "Sending…" : "Email"}</Button>
        <Button onClick={printIt} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="invoice-print-btn"><Printer className="h-4 w-4 mr-2" />Print</Button>
      </div>
      <Card className="p-8 md:p-12 print:border-0 print:shadow-none">
        <div className="flex items-start justify-between mb-8 pb-6 border-b border-border">
          <div>
            <Logo size={36} darkBackdrop={!!settings?.logo_url} />
            <div className="mt-3 text-xs text-muted-foreground">
              {settings?.business_address || "Brisbane, QLD, Australia"}<br />
              {settings?.business_phone && `${settings.business_phone} · `}{settings?.business_email || "info@eagleeye.com.au"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Invoice</div>
            <div className="font-display text-2xl font-bold">{inv.invoice_number}</div>
            <div className="text-xs text-muted-foreground mt-1">{new Date(inv.created_at).toLocaleDateString()}</div>
            <div className={`inline-block mt-2 text-xs px-3 py-1 rounded-full font-semibold ${inv.status === "paid" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
              {inv.status.toUpperCase()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Billed to</div>
            <div className="font-semibold">{inv.customer_name}</div>
            <div className="text-sm text-muted-foreground">{inv.customer_email}</div>
            <div className="text-sm text-muted-foreground">{inv.customer_phone}</div>
            {inv.address && <div className="text-sm text-muted-foreground mt-1">{inv.address}</div>}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Payment</div>
            <div className="text-sm">Method: <b className="capitalize">{(inv.payment_method || "unpaid").replace("_", " ")}</b></div>
          </div>
        </div>

        <table className="w-full mb-6 text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 text-xs uppercase tracking-widest text-muted-foreground">Description</th>
              <th className="text-right py-2 text-xs uppercase tracking-widest text-muted-foreground">Qty</th>
              <th className="text-right py-2 text-xs uppercase tracking-widest text-muted-foreground">Unit</th>
              <th className="text-right py-2 text-xs uppercase tracking-widest text-muted-foreground">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(inv.line_items || []).map((li, i) => (
              <tr key={i} className="border-b border-border">
                <td className="py-3">{li.description}</td>
                <td className="text-right py-3">{li.qty}</td>
                <td className="text-right py-3">${(li.unit_price || 0).toFixed(2)}</td>
                <td className="text-right py-3 font-semibold">${((li.qty || 0) * (li.unit_price || 0)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-72 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>${(inv.subtotal || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">GST ({Math.round((inv.tax_rate || 0) * 100)}%)</span><span>${(inv.tax || 0).toFixed(2)}</span></div>
            <div className="flex justify-between pt-2 border-t border-border font-display text-lg font-bold"><span>Total (AUD)</span><span className="text-eagle-gold">${(inv.amount || 0).toFixed(2)}</span></div>
          </div>
        </div>

        {inv.notes && (
          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Notes</div>
            <div className="text-sm whitespace-pre-wrap">{inv.notes}</div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-border text-xs text-center text-muted-foreground">
          Thank you for choosing Eagle Eye Alarm Monitoring &amp; Security Services.
        </div>
      </Card>
    </div>
  );
}